import type { Call } from '@twilio/voice-sdk';
import React, { createContext, useContext } from 'react';

import useTwilioDevice, { MicPermissionStatus, TwilioDeviceStatus } from '@/hooks/useTwilioDevice';
import { SoftphoneAddParticipantPayload } from '@/types/softphone';

export interface SoftPhoneContextInterface {
  startCall(to: string, personId?: string): Promise<Call>;
  endCall(): void;
  addParticipant(participant: SoftphoneAddParticipantPayload): Promise<void>;
  activeCall: Call | null;
  status: TwilioDeviceStatus;
  micPermission: MicPermissionStatus;
  availableInputDevices: MediaDeviceInfo[];
  availableOutputDevices: MediaDeviceInfo[];
  inputDeviceId: string | null;
  outputDeviceId: string | null;
  isOutputSelectionSupported: boolean;
  setInputDevice(deviceId: string): Promise<void>;
  setOutputDevice(deviceId: string): Promise<void>;
  enumerateDevices(): Promise<void>;
}

// null   = user has no softphone permission → hide the icon entirely
// string = 'disabled-in-embed' → show a disabled icon explaining it's unsupported here
// object = fully enabled
const SoftPhoneContext = createContext<SoftPhoneContextInterface | 'disabled-in-embed' | null>(
  null
);

// Inner component so useTwilioDevice is only mounted when actually enabled.
const SoftPhoneActiveProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    startCall,
    endCall,
    addParticipant,
    activeCall,
    status,
    micPermission,
    availableInputDevices,
    availableOutputDevices,
    inputDeviceId,
    outputDeviceId,
    isOutputSelectionSupported,
    setInputDevice,
    setOutputDevice,
    enumerateDevices
  } = useTwilioDevice();

  return (
    <SoftPhoneContext.Provider
      value={{
        startCall,
        endCall,
        addParticipant,
        activeCall,
        status,
        micPermission,
        availableInputDevices,
        availableOutputDevices,
        inputDeviceId,
        outputDeviceId,
        isOutputSelectionSupported,
        setInputDevice,
        setOutputDevice,
        enumerateDevices
      }}>
      {children}
    </SoftPhoneContext.Provider>
  );
};

export const SoftPhoneProvider = ({
  children,
  enabled = false,
  disabledInEmbed = false
}: {
  children: React.ReactNode;
  enabled?: boolean;
  disabledInEmbed?: boolean;
}) => {
  if (!enabled) {
    return <SoftPhoneContext.Provider value={null}>{children}</SoftPhoneContext.Provider>;
  }

  if (disabledInEmbed) {
    return (
      <SoftPhoneContext.Provider value="disabled-in-embed">{children}</SoftPhoneContext.Provider>
    );
  }

  return <SoftPhoneActiveProvider>{children}</SoftPhoneActiveProvider>;
};

export const useSoftPhone = () => useContext(SoftPhoneContext);

export const isSoftPhoneActive = (
  value: SoftPhoneContextInterface | 'disabled-in-embed' | null
): value is SoftPhoneContextInterface => value !== null && value !== 'disabled-in-embed';

export default SoftPhoneContext;
